import { ValidationFormat } from '@wix/ambassador-forms-v4-form-http/types';
import { getNestedFormsByTarget } from './apply-nested-forms';
/**
 * `country` selects the address template a multiline address is validated
 * against, so it is kept even when hidden — dropping it would silently
 * validate every other sub field against the default country's rules.
 */ const ADDRESS_DISCRIMINATOR = 'country';
/**
 * Returns `values` without the entries of fields hidden in `form`.
 *
 * `form` is expected to be the form with all overrides applied, so hiding by
 * override and hiding by rule are both covered. Nested forms, object arrays and
 * multiline address sub fields are walked as well.
 *
 * Hidden fields are already exempt from `required`, so omitting their values
 * only stops format checks from failing on data the user can neither see
 * nor fix.
 */ export function omitHiddenValues(form, values) {
    const nestedFormsByTarget = getNestedFormsByTarget(form);
    return (form.fields ?? []).reduce((result, field)=>{
        const target = field.target;
        if (!target || !(target in result)) {
            return result;
        }
        if (field.hidden) {
            delete result[target];
            return result;
        }
        const nestedForm = field.nestedFormId ? nestedFormsByTarget?.[target] : getObjectArrayForm(field);
        if (nestedForm) {
            result[target] = omitHiddenNestedValues(nestedForm, result[target]);
            return result;
        }
        if (isMultilineAddress(field)) {
            result[target] = omitHiddenAddressValues(field, result[target]);
        }
        return result;
    }, {
        ...values
    });
}
function omitHiddenNestedValues(form, value) {
    if (Array.isArray(value)) {
        return value.map((item)=>isPlainObject(item) ? omitHiddenValues(form, item) : item);
    }
    return isPlainObject(value) ? omitHiddenValues(form, value) : value;
}
function omitHiddenAddressValues(field, value) {
    const fieldSettings = field.view?.fieldSettings;
    if (!fieldSettings || !isPlainObject(value)) {
        return value;
    }
    const result = {
        ...value
    };
    Object.entries(fieldSettings).forEach(([subTarget, settings])=>{
        if (settings?.show === false && subTarget !== ADDRESS_DISCRIMINATOR) {
            delete result[subTarget];
        }
    });
    return result;
}
function getObjectArrayForm(field) {
    return field.validation?.predefined?.format === ValidationFormat.OBJECT_ARRAY ? {
        fields: field.view?.fields ?? []
    } : undefined;
}
function isMultilineAddress(field) {
    return field.validation?.predefined?.format === ValidationFormat.MULTILINE_ADDRESS;
}
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

//# sourceMappingURL=omit-hidden-values.js.map